import { PatientViewModel } from '../../patient/components/models/patient-view-model';
import { 
  checkDemographicsCompleteWithFormData, 
  getMissingDemographicFieldsWithFormData,
  DemographicsFormData,
} from './validation-checks';
import { disabledConvertMessages } from '../../shared/enums';
import { emailAddressValidator, phoneNumberValidator } from '../../shared/misc/regex';
import { GuarantorDetails } from '../components/guarantor-address-fields';

export interface PrePayRequirement {
  requirementName: string;
  status: boolean;
  missingFields?: string[];
  fixUrl?: string;
  fixLabel?: string;
}

export interface NewChampionGuarantorData {
  isNewChampion: boolean;
  isGuarantor: boolean;
  addressDetails: GuarantorDetails;
  label: string;
}

export interface PrePayValidationParams {
  patient: PatientViewModel | null;
  patientId?: number;
  billingEmail?: string;
  guarantorFormData?: DemographicsFormData;
  isProgramSetup?: boolean;
  downPaymentChampion?: NewChampionGuarantorData;
  recurringChampion?: NewChampionGuarantorData;
}

export interface PrePayValidationResult {
  isValid: boolean;
  requirements: PrePayRequirement[];
  failedRequirements: PrePayRequirement[];
}

/**
 * Check if billing/portal login email is valid.
 */
const checkBillingEmailComplete = (email?: string): boolean => {
  if (!email || email.trim() === '') return false;
  return emailAddressValidator.test(email);
};

/**
 * Get missing billing email field description.
 */
const getMissingBillingEmailFields = (email?: string): string[] => {
  if (!email || email.trim() === '') return ['Receipt Email'];
  if (!emailAddressValidator.test(email)) return ['Valid Receipt Email'];
  return [];
};

/**
 * Check if new champion guarantor info is complete.
 * For enrollment flow: requires phone, date of birth, relationship, and full address.
 * For non-enrollment flow: only requires date of birth.
 */
const checkNewChampionGuarantorComplete = (
  data: NewChampionGuarantorData | undefined,
  isProgramSetup: boolean
): boolean => {
  if (!data || !data.isNewChampion || !data.isGuarantor) return true;
  
  const details = data.addressDetails;
  const hasValidDob = !!(details.birthDate?.trim());

  if (!isProgramSetup) {
    return hasValidDob;
  }

  const hasValidAddress = !!(
    details.streetAddress1?.trim() &&
    details.city?.trim() &&
    details.state?.trim() &&
    details.zipCode?.trim()
  );

  const hasValidPhone = !!(
    details.phone?.trim() &&
    phoneNumberValidator.test(details.phone)
  );

  const hasValidRelationship = !!(details.relationshipToPatient?.trim());

  return hasValidAddress && hasValidPhone && hasValidDob && hasValidRelationship;
};

/**
 * Get missing fields for new champion guarantor.
 */
const getMissingNewChampionGuarantorFields = (
  data: NewChampionGuarantorData | undefined,
  isProgramSetup: boolean
): string[] => {
  if (!data || !data.isNewChampion || !data.isGuarantor) return [];
  
  const details = data.addressDetails;
  const missing: string[] = [];

  if (!details.birthDate?.trim()) {
    missing.push('Date of Birth');
  }

  if (isProgramSetup) {
    if (!details.phone?.trim() || !phoneNumberValidator.test(details.phone)) {
      missing.push('Phone Number');
    }
    if (!details.relationshipToPatient?.trim()) {
      missing.push('Relationship to Patient');
    }
    if (!details.streetAddress1?.trim()) {
      missing.push('Street Address');
    }
    if (!details.city?.trim()) {
      missing.push('City');
    }
    if (!details.state?.trim()) {
      missing.push('State');
    }
    if (!details.zipCode?.trim()) {
      missing.push('Zip Code');
    }
  }

  return missing;
};

export const validatePrePayRequirements = (
  params: PrePayValidationParams
): PrePayValidationResult => {
  const { 
    patient, 
    patientId, 
    billingEmail, 
    guarantorFormData,
    isProgramSetup = false,
    downPaymentChampion,
    recurringChampion,
  } = params;

  const demographicsStatus = checkDemographicsCompleteWithFormData(patient, guarantorFormData);
  const billingEmailStatus = checkBillingEmailComplete(billingEmail);
  const downPaymentChampionStatus = checkNewChampionGuarantorComplete(downPaymentChampion, isProgramSetup);
  const recurringChampionStatus = checkNewChampionGuarantorComplete(recurringChampion, isProgramSetup);

  // Build fix URL if patientId is provided
  const specialistDashboardUrl = patientId ? `/specialist/dashboard/${patientId}` : undefined;

  const requirements: PrePayRequirement[] = [
    {
      requirementName: disabledConvertMessages.demographicsLabel,
      status: demographicsStatus,
      missingFields: !demographicsStatus ? getMissingDemographicFieldsWithFormData(patient, guarantorFormData) : undefined,
      fixUrl: specialistDashboardUrl,
      fixLabel: 'Complete',
    },
    {
      requirementName: 'Portal Login Email is required',
      status: billingEmailStatus,
      missingFields: !billingEmailStatus ? getMissingBillingEmailFields(billingEmail) : undefined,
    },
  ];

  // Add down payment champion validation if applicable
  if (downPaymentChampion?.isNewChampion && downPaymentChampion?.isGuarantor) {
    requirements.push({
      requirementName: `Guarantor information for ${downPaymentChampion.label}`,
      status: downPaymentChampionStatus,
      missingFields: !downPaymentChampionStatus 
        ? getMissingNewChampionGuarantorFields(downPaymentChampion, isProgramSetup) 
        : undefined,
    });
  }

  // Add recurring champion validation if applicable
  if (recurringChampion?.isNewChampion && recurringChampion?.isGuarantor) {
    requirements.push({
      requirementName: `Guarantor information for ${recurringChampion.label}`,
      status: recurringChampionStatus,
      missingFields: !recurringChampionStatus 
        ? getMissingNewChampionGuarantorFields(recurringChampion, isProgramSetup) 
        : undefined,
    });
  }

  const failedRequirements = requirements.filter((req) => !req.status);
  const isValid = failedRequirements.length === 0;

  return {
    isValid,
    requirements,
    failedRequirements,
  };
};

export const arePrePayRequirementsMet = (
  params: PrePayValidationParams
): boolean => {
  return validatePrePayRequirements(params).isValid;
};
